import { flexRender, getCoreRowModel, Table, useReactTable } from '@tanstack/react-table';
import type { ColumnDef } from '@tanstack/react-table';
import * as React from 'react';
import { cn } from '../../../utils';
import {
	DEFAULT_COLUMN_MIN_WIDTH,
	DEFAULT_ROW_HEADER_WIDTH,
	HEADER_ROW_INDEX,
	ROW_HEADER_COLUMN_ID,
	STICKY_BODY_Z_INDEX,
	STICKY_HEADER_Z_INDEX,
} from './matrix.constants';
import type { IMatrixHandle, IMatrixProps, IScrollToColumnOptions } from './matrix.types';

/**
 * Generic rows × columns layout primitive with a sticky row-header column and
 * horizontally-scrollable body. Backed by `@tanstack/react-table` for its
 * row/column/header model — sorting, pagination, filtering, and row selection
 * are intentionally not enabled.
 *
 * The matrix has no opinions about cell content. Pass `renderRowHeader`,
 * `renderColumnHeader`, `renderCell`, and (optionally) `renderCornerCell` to
 * own all visual styling, hover, click handling, and a11y semantics. The
 * primitive only positions: it sets `position: sticky; left: 0; z-index` on
 * the row-header column and applies the CSS-grid template — nothing else.
 *
 * Sticky-cell background caveat: the matrix does not paint backgrounds on
 * sticky cells. The consumer's `renderRowHeader` and `renderCornerCell` output
 * MUST include a solid background, otherwise scrolling cells will show through
 * during horizontal scroll. If `renderCornerCell` is omitted entirely, the
 * top-left grid slot is rendered transparently — fine when the matrix is
 * pinned to a left edge, but the first column will be visible underneath
 * during horizontal scroll. Provide a corner cell with a solid background to
 * avoid that.
 *
 * Imperative scroll: pass a `ref<IMatrixHandle>` and call
 * `ref.current.scrollToColumn(columnId)` to bring a column into view, just past
 * the sticky row-header column. The matrix never auto-scrolls. The scroll is
 * smooth and no-ops when the target is already fully visible.
 *
 * Accessibility: rendered with `role="grid"` plus `role="row"` /
 * `role="columnheader"` / `role="rowheader"` / `role="gridcell"` on the
 * appropriate descendants, with `aria-rowcount` / `aria-colcount` on the grid.
 *
 * Direction: LTR-only. The sticky column anchors to the left and `scrollLeft`
 * math assumes LTR scroll semantics. RTL is out of scope.
 */
function MatrixInner<TRow, TColumn>(
	props: IMatrixProps<TRow, TColumn>,
	ref: React.ForwardedRef<IMatrixHandle>
): JSX.Element {
	const {
		rows,
		columns,
		getRowId,
		getColumnId,
		renderRowHeader,
		renderColumnHeader,
		renderCell,
		renderCornerCell,
		rowHeaderWidth = DEFAULT_ROW_HEADER_WIDTH,
		columnMinWidth = DEFAULT_COLUMN_MIN_WIDTH,
		className,
		'data-testid': dataTestId,
	} = props;

	const scrollContainerRef = React.useRef<HTMLDivElement | null>(null);
	const cornerCellRef = React.useRef<HTMLDivElement | null>(null);
	const columnRefs = React.useRef<Map<string, HTMLDivElement>>(new Map());

	const tableColumns: ColumnDef<TRow>[] = React.useMemo(() => {
		const headerCol: ColumnDef<TRow> = {
			id: ROW_HEADER_COLUMN_ID,
			header: () => (renderCornerCell ? renderCornerCell() : null),
			cell: ctx => renderRowHeader(ctx.row.original),
		};

		const dataCols: ColumnDef<TRow>[] = columns.map(column => ({
			id: getColumnId(column),
			header: () => renderColumnHeader(column),
			cell: ctx => renderCell(ctx.row.original, column),
		}));

		return [headerCol, ...dataCols];
	}, [columns, getColumnId, renderCornerCell, renderRowHeader, renderColumnHeader, renderCell]);

	const table: Table<TRow> = useReactTable<TRow>({
		data: rows,
		columns: tableColumns,
		getRowId,
		getCoreRowModel: getCoreRowModel(),
	});

	const gridTemplateColumns = `${rowHeaderWidth} repeat(${columns.length}, minmax(${columnMinWidth}, 1fr))`;

	React.useImperativeHandle(
		ref,
		() => ({
			scrollToColumn: (columnId: string, options?: IScrollToColumnOptions): void => {
				const target = columnRefs.current.get(columnId);
				const container = scrollContainerRef.current;
				if (!target || !container) {
					return;
				}

				const targetRect: DOMRect = target.getBoundingClientRect();
				const containerRect: DOMRect = container.getBoundingClientRect();

				// Width occupied by the sticky row-header column, measured from the DOM
				// rather than parsing `rowHeaderWidth` so non-rem units / runtime resizes
				// work correctly.
				const stickyOffsetPx: number = cornerCellRef.current?.getBoundingClientRect().width ?? 0;

				const targetLeft: number = targetRect.left - containerRect.left;
				const targetRight: number = targetRect.right - containerRect.left;
				const visibleLeftEdge: number = stickyOffsetPx;
				const visibleRightEdge: number = containerRect.width;

				const isFullyVisible: boolean = targetLeft >= visibleLeftEdge && targetRight <= visibleRightEdge;
				if (isFullyVisible) {
					return;
				}

				const targetGridOffset: number = targetRect.left - containerRect.left + container.scrollLeft;
				const nextScrollLeft: number = Math.max(0, targetGridOffset - stickyOffsetPx);

				container.scrollTo({ left: nextScrollLeft, behavior: options?.behavior ?? 'smooth' });
			},
			getScrollContainer: () => scrollContainerRef.current,
		}),
		[]
	);

	const headerGroup = table.getHeaderGroups()[0];
	const bodyRows = table.getRowModel().rows;

	const setColumnRef = React.useCallback(
		(columnId: string) =>
			(node: HTMLDivElement | null): void => {
				if (node) {
					columnRefs.current.set(columnId, node);
					return;
				}
				columnRefs.current.delete(columnId);
			},
		[]
	);

	return (
		<section className={cn('w-full min-w-0', className)} data-testid={dataTestId}>
			<div ref={scrollContainerRef} className="w-full min-w-0 overflow-x-auto overflow-y-hidden">
				<div
					role="grid"
					aria-rowcount={bodyRows.length + 1}
					aria-colcount={columns.length + 1}
					className="grid w-full min-w-max"
					style={{ gridTemplateColumns }}
				>
					<div role="row" aria-rowindex={HEADER_ROW_INDEX} style={{ display: 'contents' }}>
						{headerGroup.headers.map((header, index) => {
							const isRowHeader: boolean = index === 0;

							const stickyStyle: React.CSSProperties | undefined = isRowHeader
								? { position: 'sticky', left: 0, zIndex: STICKY_HEADER_Z_INDEX }
								: undefined;

							return (
								<div
									key={header.id}
									role="columnheader"
									aria-colindex={index + 1}
									ref={isRowHeader ? cornerCellRef : setColumnRef(header.column.id)}
									style={stickyStyle}
								>
									{flexRender(header.column.columnDef.header, header.getContext())}
								</div>
							);
						})}
					</div>
					{bodyRows.map((row, rowIndex) => (
						<div
							key={row.id}
							role="row"
							aria-rowindex={rowIndex + HEADER_ROW_INDEX + 1}
							style={{ display: 'contents' }}
						>
							{row.getVisibleCells().map((cell, index) => {
								const isRowHeader: boolean = index === 0;

								const stickyStyle: React.CSSProperties | undefined = isRowHeader
									? { position: 'sticky', left: 0, zIndex: STICKY_BODY_Z_INDEX, height: '10rem' }
									: undefined;

								return (
									<div
										key={cell.id}
										role={isRowHeader ? 'rowheader' : 'gridcell'}
										aria-colindex={index + 1}
										style={stickyStyle}
									>
										{flexRender(cell.column.columnDef.cell, cell.getContext())}
									</div>
								);
							})}
						</div>
					))}
				</div>
			</div>
		</section>
	);
}

const MatrixForwarded = React.forwardRef(MatrixInner);
MatrixForwarded.displayName = 'Matrix';

export const Matrix = MatrixForwarded as <TRow, TColumn>(
	props: IMatrixProps<TRow, TColumn> & React.RefAttributes<IMatrixHandle>
) => JSX.Element;
